import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  message,
  Dropdown,
  Spin,
  theme,
} from "antd";
import {
  DollarOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Sharp, tech-style input
const inputStyle = { borderRadius: 2 };

export default function HistoricalReceivable() {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(6, "month"),
    dayjs(),
  ]);
  const [pageSize, setPageSize] = useState(10);

  const [groupOptions, setGroupOptions] = useState([
    { value: "all", label: "All Control Groups" },
  ]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [invoiceData, setInvoiceData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: "Bearer " + localStorage.getItem("token") };

        // Fetch company ledger entries for receivable history
        const res = await fetch("/api/settlements/ledger/company?limit=200", { headers });
        if (res.ok) {
          const json = await res.json();
          if (json.r === 0 && json.data) {
            const entries = json.data.map((e, i) => ({
              id: `INV-${e.date}-${i}`,
              groupId: e.company_id || "",
              groupName: e.company_id || "Group",
              invoiceDate: e.date,
              periodStart: e.date,
              periodEnd: e.date,
              invoicedAmount: Number(e.credit) || 0,
              receivedAmount: Number(e.debit) || 0,
              receivedDate: Number(e.debit) > 0 ? e.date : null,
              status: Number(e.debit) >= Number(e.credit) ? "settled" : (Number(e.debit) > 0 ? "partial" : "pending"),
            }));
            setInvoiceData(entries);

            // Build group options from unique company_ids
            const uniqueGroups = [...new Set(json.data.map((e) => e.company_id).filter(Boolean))];
            setGroupOptions([
              { value: "all", label: "All Control Groups" },
              ...uniqueGroups.map((g) => ({ value: g, label: g })),
            ]);

            // Build monthly chart data
            const monthMap = {};
            json.data.forEach((e) => {
              const month = dayjs(e.date).format("MMM YYYY");
              if (!monthMap[month]) monthMap[month] = { month, invoiced: 0, received: 0 };
              monthMap[month].invoiced += Number(e.credit) || 0;
              monthMap[month].received += Number(e.debit) || 0;
            });
            setMonthlyData(
              Object.values(monthMap).sort(
                (a, b) => dayjs(a.month, "MMM YYYY").unix() - dayjs(b.month, "MMM YYYY").unix()
              )
            );
          }
        }
      } catch (error) {
        console.error("Failed to fetch historical receivable:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter by group
  const filteredInvoices = selectedGroup === "all"
    ? invoiceData
    : invoiceData.filter((inv) => inv.groupId === selectedGroup);

  // Calculate stats
  const totalInvoiced = filteredInvoices.reduce((sum, inv) => sum + inv.invoicedAmount, 0);
  const totalReceived = filteredInvoices.reduce((sum, inv) => sum + (inv.receivedAmount || 0), 0);
  const settledCount = filteredInvoices.filter((inv) => inv.status === "settled").length;
  const pendingCount = filteredInvoices.filter((inv) => inv.status !== "settled").length;

  const getStatusTag = (status) => {
    const config = {
      pending: { color: "default", text: "Pending" },
      partial: { color: "warning", text: "Partial" },
      settled: { color: "success", text: "Settled" },
    };
    const { color, text } = config[status] || config.pending;
    return <Tag color={color}>{text}</Tag>;
  };

  const exportMenuItems = [
    { key: "excel", label: "Export to Excel", icon: <FileExcelOutlined /> },
    { key: "csv", label: "Export to CSV", icon: <FileExcelOutlined /> },
    { key: "pdf", label: "Export to PDF", icon: <FilePdfOutlined /> },
  ];

  const handleExport = ({ key }) => {
    message.success(`Exporting to ${key.toUpperCase()}...`);
  };

  const columns = [
    {
      title: "Invoice #",
      dataIndex: "id",
      key: "id",
      render: (id) => <Text strong>{id}</Text>,
    },
    {
      title: "Control Group",
      dataIndex: "groupName",
      key: "groupName",
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.groupId}</Text>
        </Space>
      ),
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
    },
    {
      title: "Period",
      key: "period",
      render: (_, record) => (
        <Text style={{ fontSize: 12 }}>{record.periodStart} ~ {record.periodEnd}</Text>
      ),
    },
    {
      title: "Invoiced",
      dataIndex: "invoicedAmount",
      key: "invoicedAmount",
      align: "right",
      render: (amount) => (
        <Text strong style={{ color: token.colorPrimary }}>
          ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: "Received",
      dataIndex: "receivedAmount",
      key: "receivedAmount",
      align: "right",
      render: (amount) => amount
        ? <Text style={{ color: token.colorSuccess }}>
            ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </Text>
        : <Text type="secondary">-</Text>,
    },
    {
      title: "Received Date",
      dataIndex: "receivedDate",
      key: "receivedDate",
      render: (date) => date || <Text type="secondary">-</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusTag(status),
    },
  ];

  return (
    <PageLayout active="historicalReceivable">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              Historical Receivable
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              View historical receivables and payment records
            </Text>
          </div>
          <Dropdown menu={{ items: exportMenuItems, onClick: handleExport }}>
            <Button size="large" style={inputStyle}>
              Export <DownOutlined />
            </Button>
          </Dropdown>
        </div>
      </div>

      {/* Filters */}
      <Card bordered={false} style={{ marginBottom: 24, borderRadius: 12 }}>
        <Space wrap size="middle">
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Control Group
            </Text>
            <Select
              value={selectedGroup}
              onChange={setSelectedGroup}
              options={groupOptions}
              style={{ width: 200, ...inputStyle }}
              size="large"
            />
          </div>
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Date Range
            </Text>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              picker="month"
              size="large"
              style={{ width: 280, ...inputStyle }}
            />
          </div>
        </Space>
      </Card>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Invoiced"
              value={totalInvoiced}
              precision={2}
              prefix="$"
              valueStyle={{ color: token.colorPrimary }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Received"
              value={totalReceived}
              precision={2}
              prefix="$"
              valueStyle={{ color: token.colorSuccess }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Settled Invoices"
              value={settledCount}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: token.colorSuccess }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Outstanding"
              value={pendingCount}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Monthly Chart */}
      <Card
        bordered={false}
        style={{ marginBottom: 24, borderRadius: 12 }}
        title={<Text strong>Monthly Receivables vs Receipts</Text>}
      >
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <RechartsTooltip
                formatter={(value) => [`$${value.toLocaleString()}`, ""]}
              />
              <Legend />
              <Bar dataKey="invoiced" name="Invoiced" fill={token.colorPrimary} radius={[4, 4, 0, 0]} />
              <Bar dataKey="received" name="Received" fill={token.colorSuccess} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Invoice List Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title={<Text strong>Invoice History</Text>}
      >
        <Table
          columns={columns}
          dataSource={filteredInvoices}
          rowKey="id"
          loading={loading}
          pagination={makePagination(pageSize, setPageSize)}
        />
      </Card>
    </PageLayout>
  );
}
